<script setup>
import FormSection from "@/Components/FormSection.vue";
import TextInput from "@/Components/TextInput.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import SelectInput from "@/Components/SelectInput.vue";
import {useForm, usePage} from "@inertiajs/inertia-vue3";
import PhoneInput from "@/Components/PhoneInput.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import {computed, ref, watch} from "vue";

const props = defineProps({
  permissions: Object,
  userTypePermissionMap: Object,
  vendors: Array,
  companies: Array,
  userTypes: Object,
});
const userProperty = computed(() => usePage().props.value.user);
const user = userProperty.value;
let employeeTypeId = user.employee_type_id;
let userType = user.employeeType;
if (route().current('admin.users.create')) {
  const queryParameters = route().params || {};
  if (queryParameters.employeeTypeId) {
    employeeTypeId = queryParameters.employeeTypeId;
  }
  if (queryParameters.userType) {
    userType = queryParameters.userType;
  }
}
const saving = ref(false);
const currentDate = new Date();
currentDate.setHours(0, 0, 0, 0);
const dateHired = ref(user.datehired ? (user.datehired * 1000) : null);
const showPassword = ref(false);

const form = useForm({
  _method: 'POST',
  employeeType: userType || 0,
  firstname: user.firstname,
  lastname: user.lastname,
  title: user.title,
  email: user.email,
  phone: user.phone,
  cell: user.cell,
  address: user.address,
  city: user.city,
  state: user.state,
  zip: user.zip,
  salary: (user.salary || 'no').toLowerCase(),
  wage: user.wage,
  username: user.username,
  employee_type_id: employeeTypeId || null,
  datehired: dateHired.value,
  password: null,
  permission: {}
});

Object.keys(props.permissions).forEach((identifier) => {
  form.permission[identifier] = ((user.permission && user.permission[identifier]) || 'off') === 'on';
});

const ensureEditImpliesLookbookReadOnly = () => {
  if (form.permission.can_view_lookbook) {
    form.permission.lookbook_read_only = true;
  }
};
ensureEditImpliesLookbookReadOnly();

watch(userProperty, (value) => resetForm(value));
watch(() => form.employeeType, (value) => setUserTypePermissions(value));
watch(() => form.permission.can_view_lookbook, () => ensureEditImpliesLookbookReadOnly());
const setUserTypePermissions = (userType) => {
  const permissionMap = props.userTypePermissionMap[userType];
  Object.keys(form.permission).forEach(key => {
    form.permission[key] = permissionMap.includes(key);
  });
  ensureEditImpliesLookbookReadOnly();
}

const dateDisplayFormat = (date) => {
  const options = {year: 'numeric', month: 'long', day: 'numeric'};

  return date.toLocaleDateString('en-US', options);
}

const resetForm = (userData) => {
  if (Object.keys(usePage().props.value.errors).length > 0) {
    return;
  }

  user.employeeID = userData.employeeID;
  user.employeeType = userData.employeeType;
  Object.keys(form.data()).forEach(key => {
    form[key] = userData[key];
    if (key === 'permission') {
      Object.keys(props.permissions).forEach(key => {
        form.permission[key] = (userData.permission[key] || 'off') === 'on';
      });
      ensureEditImpliesLookbookReadOnly();
    }
  });
  dateHired.value = userData.datehired ? (userData.datehired * 1000) : null;
}

const saveForm = () => {
  saving.value = true;
  let endpoint = route('admin.users.store');
  if (user.employeeID) {
    form._method = 'PUT';
    endpoint = route('admin.users.update', {user: user.employeeID});
  }
  form.datehired = dateHired.value;
  if (dateHired.value !== null && String(dateHired.value).length > 11) { // Fix the conversion of the timestamp between PHP seconds and JS milliseconds
    form.datehired = form.datehired / 1000;
  }
  ensureEditImpliesLookbookReadOnly();
  Object.keys(props.permissions).forEach((identifier) => {
    form.permission[identifier] = (form.permission[identifier] === true) ? 'on' : 'off';
  });
  const element = document.getElementById('employeeType')
  form.post(endpoint, {
    preserveScroll: true,
    onSuccess() {
      element.scrollIntoView();
    },
    onError() {
      element.scrollIntoView();
    },
    onFinish() {
      saving.value = false;
    }
  });
};

const isVendorSelected = () => {
  return parseInt(form.employeeType) === 1;
}

const isClientSelected = () => {
  return parseInt(form.employeeType) === 2;
}

// Display-only: stack Lookbook Read Only above Can Edit Lookbook in the same grid column.
const displayPermissions = computed(() => {
  const list = Object.values(props.permissions);
  const lookbookIds = ['lookbook_read_only', 'can_view_lookbook'];
  const lookbook = lookbookIds
    .map((id) => list.find((permission) => permission.identifier === id))
    .filter(Boolean);
  if (lookbook.length < 2) {
    return list;
  }

  const others = list.filter((permission) => !lookbookIds.includes(permission.identifier));
  const total = others.length + lookbook.length;
  // Largest column-1 index where the pair fits one row apart (row-major 3-col grid).
  const stackAt = Math.floor((total - 4) / 3) * 3;
  if (stackAt < 0) {
    return [...others, ...lookbook];
  }

  const ordered = [];
  let otherIndex = 0;
  for (let position = 0; position < total; position++) {
    if (position === stackAt) {
      ordered.push(lookbook[0]);
    } else if (position === stackAt + 3) {
      ordered.push(lookbook[1]);
    } else {
      ordered.push(others[otherIndex++]);
    }
  }
  return ordered;
});

</script>

<template>
  <div>
    <FormSection class="mt-5">
      <template #title>
        Personal information
      </template>
      <template #description>
        These are the basic information for the user
      </template>
      <template #form>
        <!-- First name -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="employeeType" value="User Type" :required="true"/>
          <SelectInput name="employeeType" id="employeeType"
                  class="mt-1"
                  v-model="form.employeeType">
            <option v-for="(userType, identifier) in userTypes" :value="identifier"
                    v-text="userType.display_name"></option>
          </SelectInput>
          <InputError :message="form.errors.employeeType" class="mt-2"/>
        </div>
        <!-- Vendor name -->
        <div class="col-span-6 sm:col-span-4" v-if="isVendorSelected()">
          <InputLabel for="vendor_id" value="Vendor Name" :required="true"/>
          <SelectInput name="vendor_id"
                  class="mt-1"
                  v-model="form.employee_type_id">
            <option v-for="vendor in vendors" :value="vendor.vendorID" v-text="vendor.vendorName"></option>
          </SelectInput>
          <InputError :message="form.errors.employee_type_id" class="mt-2"/>
        </div>
        <!-- Company Name -->
        <div class="col-span-6 sm:col-span-4" v-if="isClientSelected()">
          <InputLabel for="client_id" value="Company Name" :required="true"/>
          <SelectInput name="client_id"
                  class="mt-1"
                  v-model="form.employee_type_id">
            <option v-for="company in companies" :value="company.ID" v-text="company.client"></option>
          </SelectInput>
          <InputError :message="form.errors.employee_type_id" class="mt-2"/>
        </div>
        <div class="sm:col-span-2" v-if="isClientSelected() && form.employee_type_id">
          <a :href="route('admin.companies.edit', {company: form.employee_type_id})" target="_blank"
             ref="noopener noreferrer" class="mt-7 inline-block">
            <PrimaryButton type="button">Edit Client</PrimaryButton>
          </a>
        </div>
        <!-- Permissions -->
        <div class="col-span-6" v-if="user.employeeID">
          <InputLabel for="permissions" value="Permissions"/>
          <div class="grid grid-cols-3 gap-2">
            <div v-for="(permission) in displayPermissions" :key="permission.identifier" class="col-span-2 sm:col-span-1">
              <label :for="permission.identifier" class="mr-5 whitespace-nowrap">
                <input
                  type="checkbox"
                  v-model="form.permission[permission.identifier]"
                  :id="permission.identifier"
                  :disabled="permission.identifier === 'lookbook_read_only' && form.permission.can_view_lookbook"
                >&nbsp;
                <span v-text="permission.display_name"></span>
              </label>
              <InputError :message="form.errors[permission.identifier]" class="mt-2"/>
            </div>
          </div>
        </div>
        <!-- First name -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="firstname" value="First Name" :required="true"/>
          <TextInput
            id="firstname"
            v-model="form.firstname"
            type="text"
            class="mt-1 block w-full"
            autocomplete="firstname"
          />
          <InputError :message="form.errors.firstname" class="mt-2"/>
        </div>
        <!-- Last name -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="lastname" value="Last Name"/>
          <TextInput
            id="lastname"
            v-model="form.lastname"
            type="text"
            class="mt-1 block w-full"
            autocomplete="lastname"
          />
          <InputError :message="form.errors.lastname" class="mt-2"/>
        </div>
        <!-- Title -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="title" value="Title"/>
          <TextInput
            id="title"
            v-model="form.title"
            type="text"
            class="mt-1 block w-full"
            autocomplete="title"
          />
          <InputError :message="form.errors.title" class="mt-2"/>
        </div>
      </template>
    </FormSection>

    <FormSection class="pt-5">
      <template #title>
        Contact information
      </template>
      <template #description>
        How to reach out the user
      </template>
      <template #form>
        <!-- Email -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="email" value="Email" :required="true"/>
          <TextInput
            id="email"
            v-model="form.email"
            type="email"
            class="mt-1 block w-full"
          />
          <InputError :message="form.errors.email" class="mt-2"/>
        </div>
        <!-- Phone -->
        <div class="col-span-6 sm:col-span-4">
          <PhoneInput v-model="form.phone"/>
          <InputError :message="form.errors.phone" class="mt-2"/>
        </div>
        <!-- Cellular -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="cell" value="Cellular"/>
          <TextInput
            id="cell"
            v-model="form.cell"
            type="text"
            class="mt-1 block w-full"
            autocomplete="cell"
          />
          <InputError :message="form.errors.cell" class="mt-2"/>
        </div>
        <!-- Address -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="address" value="Address"/>
          <TextInput
            id="address"
            v-model="form.address"
            type="text"
            class="mt-1 block w-full"
            autocomplete="address"
          />
          <InputError :message="form.errors.address" class="mt-2"/>
        </div>
        <!-- City -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="city" value="City"/>
          <TextInput
            id="city"
            v-model="form.city"
            type="text"
            class="mt-1 block w-full"
            autocomplete="city"
          />
          <InputError :message="form.errors.city" class="mt-2"/>
        </div>
        <!-- State -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="state" value="State"/>
          <TextInput
            id="state"
            v-model="form.state"
            type="text"
            class="mt-1 block w-full"
            autocomplete="state"
          />
          <InputError :message="form.errors.state" class="mt-2"/>
        </div>
        <!-- Zip -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="zip" value="Zip"/>
          <TextInput
            id="zip"
            v-model="form.zip"
            type="text"
            class="mt-1 block w-full"
            autocomplete="zip"
          />
          <InputError :message="form.errors.zip" class="mt-2"/>
        </div>
      </template>
    </FormSection>

    <FormSection class="pt-5">
      <template #title>
        Meta information
      </template>
      <template #description>
        Additional information for the user
      </template>
      <template #form>
        <!-- Date Hired -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="email" value="Date Hired"/>

          <Datepicker class="mt-1 block w-full" v-model="dateHired" :format="dateDisplayFormat"
                      model-type="timestamp" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
          <InputError :message="form.errors.datehired" class="mt-2"/>
        </div>
        <!-- Salary -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="salary" value="Salary"/>
          <SelectInput
            class="mt-1"
            v-model="form.salary">
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </SelectInput>
          <InputError :message="form.errors.salary" class="mt-2"/>
        </div>
        <!-- Wage -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="wage" value="Wage"/>
          <TextInput
            id="wage"
            v-model="form.wage"
            type="number"
            class="mt-1 block w-full"
          />
          <InputError :message="form.errors.wage" class="mt-2"/>
        </div>
      </template>
    </FormSection>

    <FormSection class="pt-5">
      <template #title>
        Authentication information
      </template>
      <template #description>
        Login credentials for the platform
      </template>
      <template #form>
        <!-- Username -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="username" value="Username" :required="true"/>
          <TextInput
            id="username"
            v-model="form.username"
            type="text"
            class="mt-1 block w-full"
          />
          <InputError :message="form.errors.username" class="mt-2"/>
        </div>
        <!-- Password -->
        <div class="col-span-6 sm:col-span-4">
          <InputLabel for="password" value="Password" :required="true"/>
          <TextInput
            id="password"
            v-model="form.password"
            :type="(showPassword === false) ? 'password' : 'text'"
            class="mt-1 block w-full"
          />
          <InputError :message="form.errors.password" class="mt-2"/>
          <label for="show-password"><input type="checkbox" id="show-password" v-model="showPassword"/> Show
            Password</label>
          <small class="block">Minimum 8 characters.</small>
          <small class="block">At least one number.</small>
          <small class="block">At least one special character.</small>
        </div>
      </template>
      <template #actions>
        <a :href="route('admin.users.active')" class="mr-5" v-if="!saving">
          <SecondaryButton type="button">Back</SecondaryButton>
        </a>
        <PrimaryButton type="button" @click.prevent="saveForm" :disabled="saving">Save</PrimaryButton>
      </template>
    </FormSection>
  </div>
</template>
